import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Helmet } from "react-helmet";
import { Container, Modal } from "reactstrap";

import { Heading } from "components/Heading";
import { CustomButton } from "components/CustomButton";
import { PageHeadings, PageTitles } from "../../constants";
import {
  erasureCancelApi,
  erasureDeletedAccountsApi,
  erasureEraseApi,
  erasurePreviewApi,
  erasureRecordRequestApi,
  erasureRequestsApi,
} from "../../api/api.service";
// The card, row and dialog styles are Align Users' own, shared so the two staff tools look like one
// console; everything particular to this page is in its own sheet.
import "../AlignUsers/Styles.scss";
import "./Styles.scss";

/**
 * Permanently erasing a deleted account, on request.
 *
 * The public privacy pages promise: after an account is deleted, everything in it is permanently
 * erased within 60 days of a request from the teacher, their school or their district. Nothing here
 * happens on its own. A request is recorded first (which starts the 60 days from the day it
 * arrived), and erasing shows exactly what will be deleted, cleared and kept before it asks for the
 * account's email to be typed. The server checks every one of those things again when it runs.
 */

const DAY_MS = 24 * 60 * 60 * 1000;

// The API sends lists and objects as they are (a bare array, a plain object); some older routes wrap
// them as { data }. Read either, so the screen never shows "nothing" for an answer it received.
const unwrap = (res) => (res && !Array.isArray(res) && typeof res === "object" && "data" in res ? res.data : res);
const asList = (res) => {
  const body = unwrap(res);
  return Array.isArray(body) ? body : [];
};

const REQUESTERS = [
  { value: "TEACHER", label: "The teacher" },
  { value: "SCHOOL", label: "Their school" },
  { value: "DISTRICT", label: "Their district" },
];
const requesterLabel = (v) => REQUESTERS.find((r) => r.value === v)?.label ?? v;

// A request is for one deleted account, or (asked for in District Hub at contract end) a whole district.
const isDistrict = (r) => r?.scope === "DISTRICT";
const requestName = (r) =>
  isDistrict(r) ? `District: ${r.district?.name ?? `#${r.districtId}`}` : r?.account?.name ?? `Account ${r?.userId}`;
// What the reviewer types to confirm: the account's email, or the district's name.
const confirmText = (r) => (isDistrict(r) ? r.district?.name ?? "" : r?.account?.email ?? "");

const GROUPS = [
  { key: "deleted", title: "Deleted", note: "The person's own data, gone for good.", districtNote: "The district's data, gone for good." },
  {
    key: "cleared",
    title: "Name removed",
    note: "Someone else's record; only who it was goes.",
    districtNote: "Records that stay, with the link to the district removed.",
  },
  {
    key: "kept",
    title: "Kept, anonymous",
    note: "The school's class history and payment records.",
    districtNote: "The public school directory and payment records.",
  },
];

const plural = (n, one, many) => `${n.toLocaleString()} ${n === 1 ? one : many}`;
const day = (iso) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "—";
const today = () => new Date().toISOString().slice(0, 10);

/** Days until the promise runs out; negative once overdue. */
const daysLeft = (dueAt) => Math.ceil((new Date(dueAt).getTime() - Date.now()) / DAY_MS);

const DueBadge = ({ dueAt }) => {
  const left = daysLeft(dueAt);
  if (left < 0) {
    return <span className="de-due de-due--late">Overdue by {plural(-left, "day", "days")}</span>;
  }
  return (
    <span className={`de-due${left <= 14 ? " de-due--soon" : ""}`}>
      {left === 0 ? "Due today" : `${plural(left, "day", "days")} left`}
    </span>
  );
};

/** Same dialog as Align Users: own close button, dimmed backdrop, soft entrance. */
const Dialog = ({ isOpen, onDismiss, onClosed, labelledBy, wide, children }) => (
  <Modal
    isOpen={isOpen}
    toggle={onDismiss}
    onClosed={onClosed}
    centered
    size={wide ? "lg" : undefined}
    labelledBy={labelledBy}
    modalClassName="align-modal de-modal"
    backdropClassName="align-backdrop"
  >
    <div className="au-dialog">
      <button type="button" className="au-x" aria-label="Close" onClick={onDismiss}>
        <i className="mdi mdi-close" aria-hidden="true" />
      </button>
      {children}
    </div>
  </Modal>
);

const totals = (summary) => {
  const out = { deleted: 0, cleared: 0, kept: 0 };
  Object.entries(summary ?? {}).forEach(([key, n]) => {
    const group = key.split(":")[0];
    if (group in out) out[group] += n;
  });
  return out;
};

// ---- tab: open requests --------------------------------------------------------------------------

const Requests = ({ rows, loading, onErase, onCancel }) => {
  if (loading) return <div className="au-note">Loading…</div>;
  if (!rows.length) {
    return (
      <div className="de-empty">
        No open requests. When a teacher, school or district asks for a deleted account to be erased, record it
        under <strong>Deleted accounts</strong>.
      </div>
    );
  }
  return (
    <ul className="de-list" aria-label="Open requests, soonest due first">
      {rows.map((r) => (
        <li key={r.id} className="au-card de-row">
          <div className="de-row-main">
            <div className="de-name">{requestName(r)}</div>
            <div className="au-sub">
              {isDistrict(r) ? "Everything the district built up, at the end of its agreement" : r.account?.email}
            </div>
            <div className="de-meta">
              <span>
                Asked by <strong>{requesterLabel(r.requestedBy).toLowerCase()}</strong> on {day(r.receivedAt)}
              </span>
              <span className="de-ref">{r.reference}</span>
            </div>
          </div>
          <div className="de-row-side">
            <DueBadge dueAt={r.dueAt} />
            <div className="au-sub">Due {day(r.dueAt)}</div>
            <div className="de-actions">
              <CustomButton title="Cancel request" color="light" className="btn-sm" onClick={() => onCancel(r)} />
              <CustomButton title="Erase permanently" color="danger" className="btn-sm" onClick={() => onErase(r)} />
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
};

// ---- tab: deleted accounts -----------------------------------------------------------------------

const DeletedAccounts = ({ onRecord, refreshKey }) => {
  const [text, setText] = useState("");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(true);

  // Debounced; an answer for words since changed is dropped. Empty lists the most recently deleted.
  useEffect(() => {
    let live = true;
    setBusy(true);
    const t = setTimeout(async () => {
      try {
        const res = await erasureDeletedAccountsApi({ q: text.trim() });
        if (live) setRows(asList(res));
      } catch (e) {
        if (live) {
          setRows([]);
          toast.error(e?.message || "The search failed.");
        }
      } finally {
        if (live) setBusy(false);
      }
    }, 300);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [text, refreshKey]);

  return (
    <div>
      <label className="form-label font-size-13 mb-2" htmlFor="de-search">
        Find a deleted account
      </label>
      <input
        id="de-search"
        className="form-control au-input de-search"
        autoComplete="off"
        maxLength={100}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Name or email"
      />
      <ul className="de-list mt-3" aria-label="Deleted accounts" aria-busy={busy}>
        {busy && <li className="au-note">Searching…</li>}
        {!busy && rows.length === 0 && <li className="au-note">No deleted account matches.</li>}
        {!busy &&
          rows.map((u) => (
            <li key={u.id} className="au-card de-row">
              <div className="de-row-main">
                <div className="de-name">{u.name}</div>
                <div className="au-sub">{u.email}</div>
                <div className="de-meta">
                  {u.school ? <span>{u.school}</span> : null}
                  <span>Deleted {day(u.deletedAt)}</span>
                </div>
              </div>
              <div className="de-row-side">
                {u.openRequest ? (
                  <>
                    <DueBadge dueAt={u.openRequest.dueAt} />
                    <div className="au-sub">Request on file</div>
                  </>
                ) : (
                  <CustomButton title="Record request" color="primary" className="btn-sm" onClick={() => onRecord(u)} />
                )}
              </div>
            </li>
          ))}
      </ul>
    </div>
  );
};

// ---- tab: history --------------------------------------------------------------------------------

const History = ({ rows, loading }) => {
  if (loading) return <div className="au-note">Loading…</div>;
  if (!rows.length) return <div className="de-empty">Nothing has been erased yet.</div>;
  return (
    <div className="table-responsive">
      <table className="table de-table">
        <caption className="visually-hidden">Erased accounts. Names and emails are gone with the data.</caption>
        <thead>
          <tr>
            <th scope="col">Account</th>
            <th scope="col">Asked by</th>
            <th scope="col">Received</th>
            <th scope="col">Erased</th>
            <th scope="col">By</th>
            <th scope="col">Rows deleted / name removed / kept</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const t = totals(r.summary);
            return (
              <tr key={r.id}>
                <td>{isDistrict(r) ? requestName(r) : `#${r.userId}`}</td>
                <td>{requesterLabel(r.requestedBy)}</td>
                <td>{day(r.receivedAt)}</td>
                <td>{day(r.erasedAt)}</td>
                <td>{r.erasedBy ?? "—"}</td>
                <td>
                  {t.deleted.toLocaleString()} / {t.cleared.toLocaleString()} / {t.kept.toLocaleString()}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ---- the page ------------------------------------------------------------------------------------

const TABS = [
  { id: "requests", label: "Requests" },
  { id: "accounts", label: "Deleted accounts" },
  { id: "history", label: "History" },
];

const DataErasure = () => {
  const [tab, setTab] = useState("requests");
  const [open, setOpen] = useState([]);
  const [done, setDone] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accountsKey, setAccountsKey] = useState(0);

  // record a request
  const [recordFor, setRecordFor] = useState(null);
  const [form, setForm] = useState({ requestedBy: "TEACHER", receivedAt: today(), reference: "" });
  const [saving, setSaving] = useState(false);

  // erase
  const [eraseFor, setEraseFor] = useState(null);
  const [preview, setPreview] = useState(null);
  const [typed, setTyped] = useState("");
  const [erasing, setErasing] = useState(false);
  const [result, setResult] = useState(null);

  // cancel
  const [cancelFor, setCancelFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [o, d] = await Promise.all([erasureRequestsApi({ status: "OPEN" }), erasureRequestsApi({ status: "DONE" })]);
      setOpen(asList(o));
      setDone(asList(d));
    } catch (e) {
      toast.error(e?.message || "Could not load erasure requests.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // The review, whenever an erase is started. An answer for a request since closed is dropped.
  useEffect(() => {
    setPreview(null);
    setTyped("");
    if (!eraseFor) return undefined;
    let live = true;
    (async () => {
      try {
        const res = await erasurePreviewApi(eraseFor.id);
        if (live) setPreview(unwrap(res) ?? null);
      } catch (e) {
        if (live) {
          toast.error(e?.message || "Could not work out what would be erased.");
          setEraseFor(null);
        }
      }
    })();
    return () => {
      live = false;
    };
  }, [eraseFor]);

  // The server's preview says what to type; until it arrives, the request does.
  const email = preview?.confirmWith ?? confirmText(eraseFor);
  const matches = typed.trim().toLowerCase() === email.trim().toLowerCase() && email !== "";

  const record = async () => {
    if (!recordFor || form.reference.trim().length < 3) return;
    setSaving(true);
    try {
      await erasureRecordRequestApi({ userId: recordFor.id, ...form, reference: form.reference.trim() });
      toast.success(`Request recorded. ${recordFor.name}'s account must be erased within 60 days of ${day(form.receivedAt)}.`);
      setRecordFor(null);
      setAccountsKey((k) => k + 1);
      setTab("requests");
      load();
    } catch (e) {
      toast.error(e?.message || "Could not record the request.");
    } finally {
      setSaving(false);
    }
  };

  const erase = async () => {
    if (!eraseFor || !matches) return;
    setErasing(true);
    try {
      const res = await erasureEraseApi(eraseFor.id, typed.trim());
      const body = unwrap(res);
      setResult({ name: requestName(eraseFor), ...totals(body?.summary), files: body?.filesDeleted ?? 0 });
      setEraseFor(null);
      load();
    } catch (e) {
      // Nothing was erased: the server refuses before it touches anything, or rolls it all back.
      toast.error(e?.message || "Nothing was erased.");
    } finally {
      setErasing(false);
    }
  };

  const cancel = async () => {
    if (!cancelFor) return;
    try {
      await erasureCancelApi(cancelFor.id);
      toast.success("Request cancelled. Nothing was erased.");
      setCancelFor(null);
      load();
    } catch (e) {
      toast.error(e?.message || "Could not cancel the request.");
    }
  };

  const overdue = open.filter((r) => daysLeft(r.dueAt) < 0).length;

  return (
    <div className="page-content align-users data-erasure">
      <Helmet>
        <title>{PageTitles.DATA_ERASURE}</title>
      </Helmet>
      <Container fluid>
        <Heading heading={PageHeadings.DATA_ERASURE} />
        <p className="au-lede">
          When a teacher, their school or their district asks for a deleted account to be erased, record the request
          here and erase it within 60 days of the day it arrived. Erasing removes everything that identifies the
          person. The school&apos;s class history and payment records stay, with no name attached.
        </p>

        {overdue > 0 && (
          <div className="au-alert au-alert--danger" role="alert">
            <i className="mdi mdi-alert-circle-outline" aria-hidden="true" />
            <span>
              {plural(overdue, "request is", "requests are")} past the 60 days we promise. Erase{" "}
              {overdue === 1 ? "it" : "them"} now.
            </span>
          </div>
        )}

        <div className="de-tabs" role="tablist" aria-label="Data erasure">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              id={`de-tab-${t.id}`}
              aria-selected={tab === t.id}
              aria-controls={`de-panel-${t.id}`}
              className={`de-tab${tab === t.id ? " is-active" : ""}`}
              onClick={() => setTab(t.id)}
            >
              {t.label}
              {t.id === "requests" && open.length > 0 && <span className="de-count">{open.length}</span>}
            </button>
          ))}
        </div>

        <div className="de-panel" role="tabpanel" id={`de-panel-${tab}`} aria-labelledby={`de-tab-${tab}`}>
          {tab === "requests" && (
            <Requests rows={open} loading={loading} onErase={setEraseFor} onCancel={setCancelFor} />
          )}
          {tab === "accounts" && (
            <DeletedAccounts
              refreshKey={accountsKey}
              onRecord={(u) => {
                setForm({ requestedBy: "TEACHER", receivedAt: today(), reference: "" });
                setRecordFor(u);
              }}
            />
          )}
          {tab === "history" && <History rows={done} loading={loading} />}
        </div>

        {/* ---- record a request ---- */}
        <Dialog isOpen={!!recordFor} onDismiss={() => !saving && setRecordFor(null)} labelledBy="de-record-title">
          <span className="au-badge" aria-hidden="true">
            <i className="mdi mdi-file-document-edit-outline" />
          </span>
          <h5 id="de-record-title" className="au-title">
            Record a request to erase {recordFor?.name}
          </h5>
          <p className="au-text">The 60 days run from the day the request arrived, not from today.</p>
          <form
            className="de-form"
            onSubmit={(e) => {
              e.preventDefault();
              record();
            }}
          >
            <fieldset>
              <legend className="form-label font-size-13">Who asked</legend>
              <div className="de-choices">
                {REQUESTERS.map((r) => (
                  <label key={r.value} className={`de-choice${form.requestedBy === r.value ? " is-on" : ""}`}>
                    <input
                      type="radio"
                      name="requestedBy"
                      value={r.value}
                      checked={form.requestedBy === r.value}
                      onChange={() => setForm((f) => ({ ...f, requestedBy: r.value }))}
                    />
                    {r.label}
                  </label>
                ))}
              </div>
            </fieldset>
            <label className="form-label font-size-13" htmlFor="de-received">
              Date the request arrived
            </label>
            <input
              id="de-received"
              type="date"
              className="form-control au-input"
              max={today()}
              value={form.receivedAt}
              onChange={(e) => setForm((f) => ({ ...f, receivedAt: e.target.value }))}
              required
            />
            <label className="form-label font-size-13" htmlFor="de-reference">
              Where it came from
            </label>
            <input
              id="de-reference"
              className="form-control au-input"
              maxLength={300}
              value={form.reference}
              onChange={(e) => setForm((f) => ({ ...f, reference: e.target.value }))}
              placeholder="For example: email from the principal, 3 Nov, subject “Account removal”"
              aria-describedby="de-reference-help"
              required
            />
            <div id="de-reference-help" className="au-sub">
              Say where to find the request. Don&apos;t copy personal details into it.
            </div>
            <div className="au-actions">
              <CustomButton
                title="Cancel"
                color="light"
                type="button"
                className="au-modal-btn"
                onClick={() => setRecordFor(null)}
                disabled={saving}
              />
              <CustomButton
                title="Record request"
                color="primary"
                className="au-modal-btn"
                type="submit"
                loading={saving}
                disabled={form.reference.trim().length < 3 || !form.receivedAt}
              />
            </div>
          </form>
        </Dialog>

        {/* ---- review and erase ---- */}
        <Dialog isOpen={!!eraseFor} onDismiss={() => !erasing && setEraseFor(null)} labelledBy="de-erase-title" wide>
          <span className="au-badge de-badge--danger" aria-hidden="true">
            <i className="mdi mdi-delete-forever-outline" />
          </span>
          <h5 id="de-erase-title" className="au-title">
            Permanently erase {isDistrict(eraseFor) ? `${eraseFor?.district?.name}'s data` : eraseFor?.account?.name}?
          </h5>
          <p className="au-text">This can&apos;t be undone. Here is exactly what happens:</p>
          {!preview ? (
            <div className="au-working" role="status">
              <span className="au-spin" aria-hidden="true" /> Working out what would be erased…
            </div>
          ) : (
            <div className="de-groups">
              {GROUPS.map((g) => {
                const items = preview.preview[g.key] ?? [];
                const total = items.reduce((n, i) => n + i.rows, 0);
                return (
                  <section key={g.key} className={`de-group de-group--${g.key}`} aria-label={g.title}>
                    <div className="de-group-head">
                      <span className="de-group-title">{g.title}</span>
                      <span className="de-group-total">{plural(total, "row", "rows")}</span>
                    </div>
                    <div className="au-sub">{isDistrict(eraseFor) ? g.districtNote : g.note}</div>
                    <ul>
                      {items.length === 0 && <li className="au-sub">Nothing.</li>}
                      {items.map((i) => (
                        <li key={`${i.table}-${i.label}`}>
                          <span>{i.label}</span>
                          <span className="de-n">{i.rows.toLocaleString()}</span>
                        </li>
                      ))}
                    </ul>
                  </section>
                );
              })}
            </div>
          )}
          {isDistrict(eraseFor) ? (
            <p className="au-text mt-3">
              Teachers&apos; own accounts stay open, no longer attached to the district&apos;s schools. The schools stay in
              the public directory, and payment records stay without the names on them.
            </p>
          ) : (
            <p className="au-text mt-3">
              The account row stays as <strong>&ldquo;Erased account&rdquo;</strong> so the kept records have an owner.
              Nothing in it identifies {eraseFor?.account?.name ?? "them"}.
            </p>
          )}
          <label className="form-label font-size-13" htmlFor="de-confirm">
            Type <strong>{email}</strong> to confirm
          </label>
          <input
            id="de-confirm"
            className="form-control au-input"
            autoComplete="off"
            spellCheck={false}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            aria-invalid={typed !== "" && !matches}
          />
          <div className="au-actions">
            <CustomButton title="Keep the data" color="light" className="au-modal-btn" onClick={() => setEraseFor(null)} disabled={erasing} />
            <CustomButton
              title="Erase permanently"
              color="danger"
              className="au-modal-btn"
              loading={erasing}
              disabled={!matches || !preview}
              onClick={erase}
            />
          </div>
        </Dialog>

        {/* ---- done ---- */}
        <Dialog isOpen={!!result} onDismiss={() => setResult(null)} labelledBy="de-done-title">
          {result && (
            <div role="status">
              <span className="au-badge" aria-hidden="true">
                <svg viewBox="0 0 24 24">
                  <path className="au-tick" d="M5 12.5l4.5 4.5L19 7.5" />
                </svg>
              </span>
              <h5 id="de-done-title" className="au-title">
                Erased.
              </h5>
              <p className="au-text">Nothing that identifies {result.name ?? "them"} is left. It&apos;s in History.</p>
              <ul className="au-facts">
                <li>
                  <i className="mdi mdi-check" aria-hidden="true" />
                  <span>{plural(result.deleted, "row", "rows")} deleted.</span>
                </li>
                <li>
                  <i className="mdi mdi-check" aria-hidden="true" />
                  <span>Their name removed from {plural(result.cleared, "record", "records")}.</span>
                </li>
                <li>
                  <i className="mdi mdi-check" aria-hidden="true" />
                  <span>{plural(result.kept, "class or payment record", "class and payment records")} kept, anonymous.</span>
                </li>
                {result.files > 0 && (
                  <li>
                    <i className="mdi mdi-check" aria-hidden="true" />
                    <span>{plural(result.files, "uploaded file", "uploaded files")} removed from storage.</span>
                  </li>
                )}
              </ul>
              <div className="au-actions">
                <CustomButton title="Done" color="primary" className="au-modal-btn" onClick={() => setResult(null)} />
              </div>
            </div>
          )}
        </Dialog>

        {/* ---- cancel ---- */}
        <Dialog isOpen={!!cancelFor} onDismiss={() => setCancelFor(null)} labelledBy="de-cancel-title">
          <h5 id="de-cancel-title" className="au-title">
            Cancel the request for {requestName(cancelFor)}?
          </h5>
          <p className="au-text">Only do this if whoever asked has withdrawn it. Nothing is erased.</p>
          <div className="au-actions">
            <CustomButton title="Keep the request" color="light" className="au-modal-btn" onClick={() => setCancelFor(null)} />
            <CustomButton title="Cancel request" color="primary" className="au-modal-btn" onClick={cancel} />
          </div>
        </Dialog>
      </Container>
    </div>
  );
};

export default DataErasure;
